import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Stack,
  Paper,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Header from './components/Header';
import ReviewComponent from './components/ReviewComponent';
import { logUserEvent } from './utils/logger';

function TextbookViewer() {
  const { courseId } = useParams();
  const [chapters, setChapters] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null); // when set, serve PDF instead of HTML chapters
  const [currentIndex, setCurrentIndex] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [textbookCompleted, setTextbookCompleted] = useState(false);
  const [canMarkComplete, setCanMarkComplete] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        // Decide what to serve: an uploaded PDF takes precedence over the HTML textbook.
        const availRes = await axios.get(`/api/student/courses/${courseId}/media_availability`);
        const hasPdf = !!availRes.data?.has_pdf;

        // Progress is best-effort; failing to load it shouldn't block the textbook.
        try {
          const progressRes = await axios.get(`/api/student/courses/${courseId}/progress`);
          if (!cancelled) setTextbookCompleted(!!progressRes.data?.textbook_completed);
        } catch (pe) {
          console.error('Failed to load progress', pe);
        }

        if (hasPdf) {
          const mediaRes = await axios.get(`/api/student/courses/${courseId}/media/pdf`);
          if (!cancelled) {
            setPdfUrl(mediaRes.data?.url || null);
            // PDF can't report scroll-to-end, so the "mark as read" action is always available.
            setCanMarkComplete(true);
          }
        } else {
          const chaptersRes = await axios.get(`/api/student/courses/${courseId}/textbook/chapters`);
          if (!cancelled) {
            setChapters(chaptersRes.data || []);
            setCurrentIndex(1);
          }
        }

        if (!cancelled) {
          logUserEvent('material_access', 'Accessed textbook', { course_id: courseId, material_type: hasPdf ? 'pdf' : 'textbook' });
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setError('Failed to load the textbook.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadData();
    return () => { cancelled = true; };
  }, [courseId]);

  // Listen for scroll events from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'CHAPTER_SCROLLED_TO_BOTTOM') {
        // If we are at the last chapter and not yet completed
        if (chapters.length > 0 && currentIndex === chapters.length && !textbookCompleted) {
          if (!canMarkComplete) {
            setCanMarkComplete(true);
            setToastMessage("You've finished the textbook! Mark as complete to unlock the exam.");
            setShowToast(true);
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [currentIndex, chapters.length, textbookCompleted, canMarkComplete]);

  const handleMarkComplete = async () => {
    try {
      await axios.post(`/api/student/courses/${courseId}/progress`, {
        textbook_completed: true
      });
      setTextbookCompleted(true);
      setToastMessage("Great job! Textbook marked as complete.");
      setShowToast(true);
    } catch (e) {
      console.error("Failed to mark complete", e);
      setToastMessage("Failed to save progress. Please try again.");
      setShowToast(true);
    }
  };

  const total = chapters.length || 0;
  const canPrev = currentIndex > 1;
  const canNext = currentIndex < total;
  const currentTitle = chapters.find(c => c.index === currentIndex)?.title || `Chapter ${currentIndex}`;
  const isLastChapter = total > 0 && currentIndex === total;

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
    }}>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton component={RouterLink} to="/dashboard" aria-label="Back" sx={{ color: 'white' }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" component="h1" sx={{
            flexGrow: 1,
            color: 'white',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
          }}>
            Text Book
          </Typography>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        ) : pdfUrl ? (
          <>
            <Box sx={{ height: '78vh', borderRadius: 1, overflow: 'hidden', boxShadow: 1 }}>
              {/* Uploaded PDF rendered by the browser's native viewer */}
              <iframe
                title="Textbook PDF"
                src={pdfUrl}
                style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
              />
            </Box>

            <Paper sx={{ p: 2, mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'background.paper' }}>
              {textbookCompleted ? (
                <Button variant="contained" color="success" startIcon={<CheckCircleIcon />}>
                  Textbook Completed
                </Button>
              ) : (
                <Button variant="contained" color="primary" onClick={handleMarkComplete}>
                  Mark as Read
                </Button>
              )}
            </Paper>
          </>
        ) : total === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>No chapters available yet for this course.</Alert>
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Previous chapter">
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<NavigateBeforeIcon />}
                      onClick={() => {
                        setCurrentIndex(i => Math.max(1, i - 1));
                        // Reset mark complete trigger if they go back?
                        // setCanMarkComplete(false); // Optional: enforce scrolling again?
                      }}
                      disabled={!canPrev}
                    >
                      Prev
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip title="Next chapter">
                  <span>
                    <Button
                      variant="outlined"
                      endIcon={<NavigateNextIcon />}
                      onClick={() => setCurrentIndex(i => Math.min(total, i + 1))}
                      disabled={!canNext}
                    >
                      Next
                    </Button>
                  </span>
                </Tooltip>
              </Stack>

              <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                {currentTitle}
              </Typography>

              <Box sx={{ width: 120 }} />
            </Paper>

            <Box sx={{ height: '70vh', borderRadius: 1, overflow: 'hidden', boxShadow: 1 }}>
              {/* Use iframe to render single-chapter HTML served by backend */}
              <iframe
                title={`Chapter ${currentIndex}`}
                src={`/api/student/courses/${courseId}/textbook/chapter/${currentIndex}`}
                style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
              />
            </Box>

            {/* Completion Action Bar */}
            {isLastChapter && (
              <Paper sx={{ p: 2, mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'background.paper' }}>
                {textbookCompleted ? (
                  <Button variant="contained" color="success" startIcon={<CheckCircleIcon />}>
                    Textbook Completed
                  </Button>
                ) : (
                  <Tooltip title={canMarkComplete ? "Click to finish" : "Read to the end to unlock"}>
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={!canMarkComplete}
                        onClick={handleMarkComplete}
                      >
                        Mark as Complete
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Paper>
            )}
          </>
        )}

        {/* Review Component - only shown for @deepalert.ai users */}
        <ReviewComponent courseId={courseId} materialType="textbook" />

        <Snackbar
          open={showToast}
          autoHideDuration={6000}
          onClose={() => setShowToast(false)}
          message={toastMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Container>
    </Box>
  );
}

export default TextbookViewer;
